import { createSignal } from 'solid-js';

export type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface ConsoleEntry {
  id: number;
  level: ConsoleLevel;
  time: number;
  text: string;
}

const MAX_ENTRIES = 1000;
const LEVELS: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug'];

const STORAGE_KEY = 'dev-console-entries';

// Entries are kept in sessionStorage so they survive page reloads (cleared when the tab closes)
const load = (): ConsoleEntry[] => {
  if (!import.meta.env.DEV) return [];
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persist = (list: ConsoleEntry[]) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage full or unavailable - drop the older half and try once more
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(Math.floor(list.length / 2))));
    } catch {
      // give up
    }
  }
};

const initial = load();
const [entries, setEntries] = createSignal<ConsoleEntry[]>(initial);
export { entries };

export const clearEntries = () => {
  setEntries([]);
  persist([]);
};

let nextId = initial.reduce((max, e) => Math.max(max, e.id), 0) + 1;
let installed = false;

const stringify = (arg: unknown): string => {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return arg.stack || `${arg.name}: ${arg.message}`;
  if (typeof arg === 'undefined') return 'undefined';
  if (typeof arg === 'function') return arg.toString();
  try {
    const seen = new WeakSet();
    return JSON.stringify(
      arg,
      (_key, value) => {
        if (typeof value === 'bigint') return `${value}n`;
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) return '[Circular]';
          seen.add(value);
        }
        return value;
      },
      2,
    );
  } catch {
    return String(arg);
  }
};

const add = (level: ConsoleLevel, args: unknown[]) => {
  const entry: ConsoleEntry = { id: nextId++, level, time: Date.now(), text: args.map(stringify).join(' ') };
  setEntries((prev) => {
    const next = [...prev, entry];
    const trimmed = next.length > MAX_ENTRIES ? next.slice(next.length - MAX_ENTRIES) : next;
    persist(trimmed);
    return trimmed;
  });
};

/**
 * Patches console methods and global error handlers so all output is
 * recorded. Only active in dev mode; a no-op in production builds.
 */
export const installConsoleCapture = () => {
  if (!import.meta.env.DEV || installed) return;
  installed = true;

  LEVELS.forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      original(...args);
      // Defer so signal updates never run inside a reactive computation
      queueMicrotask(() => add(level, args));
    };
  });

  // console.assert only logs when the assertion fails, so it isn't covered by LEVELS
  const originalAssert = console.assert.bind(console);
  console.assert = (condition?: boolean, ...args: unknown[]) => {
    originalAssert(condition, ...args);
    if (!condition) queueMicrotask(() => add('error', ['Assertion failed:', ...args]));
  };

  // Capture phase so we also see resource load errors (img/script/link), which don't bubble
  window.addEventListener(
    'error',
    (e: Event) => {
      const target = e.target as (HTMLElement & { src?: string; href?: string }) | Window | null;
      if (target && target !== window && 'tagName' in target) {
        add('error', [`Failed to load <${target.tagName.toLowerCase()}>:`, target.src || target.href || '']);
        return;
      }
      const err = e as ErrorEvent;
      add('error', [
        `Uncaught ${err.error?.name || 'Error'}: ${err.message}`,
        err.error?.stack || (err.filename ? `(${err.filename}:${err.lineno}:${err.colno})` : ''),
      ]);
    },
    true,
  );

  window.addEventListener('unhandledrejection', (e) => {
    add('error', ['Unhandled promise rejection:', e.reason]);
  });

  window.addEventListener('securitypolicyviolation', (e) => {
    add('error', [`CSP violation: ${e.violatedDirective}`, e.blockedURI]);
  });

  // Log failed network requests (network errors and non-2xx responses)
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const [input, init] = args;
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method || (typeof input === 'object' && 'method' in input ? input.method : 'GET');
    try {
      const response = await originalFetch(...args);
      if (!response.ok) {
        add('error', [`Fetch failed: ${method} ${url} -> ${response.status} ${response.statusText}`]);
      }
      return response;
    } catch (err) {
      add('error', [`Fetch error: ${method} ${url}`, err]);
      throw err;
    }
  };
};
